import { MessageType, PushNotificationEventType } from '@/enums';
import type { InputLiveStatusResponse } from '@/models/input';
import { FuelToolsApiService } from '@/services/FuelToolsApi/fuelToolsApi.service';

export interface InputStatusUpdatePayload {
  inputId: string;
  overallStatus?: string;
  sourceStatus?: string;
}

function readOverallStatus(data?: Record<string, unknown> | null): string | undefined {
  if (!data) {
    return undefined;
  }

  return (data.overallStatus ?? data.OverallStatus) as string | undefined;
}

function readSourceStatus(data?: Record<string, unknown> | null): string | undefined {
  if (!data) {
    return undefined;
  }

  return (data.sourceStatus ?? data.SourceStatus) as string | undefined;
}

export function parseInputStatusUpdateMessage(message: unknown): InputStatusUpdatePayload | null {
  if (!message || typeof message !== 'object') {
    return null;
  }

  const msg = message as { messageType?: string; data?: Record<string, unknown> };
  if (msg.messageType !== MessageType.Notification || !msg.data) {
    return null;
  }

  if (msg.data.eventType !== PushNotificationEventType.InputStatusUpdate) {
    return null;
  }

  const inputId = (msg.data.id ?? msg.data.inputId) as string | undefined;
  if (!inputId) {
    return null;
  }

  return {
    inputId,
    overallStatus: readOverallStatus(msg.data),
    sourceStatus: readSourceStatus(msg.data),
  };
}

export async function resolveInputStatusUpdate(
  payload: InputStatusUpdatePayload
): Promise<InputLiveStatusResponse & { inputId: string }> {
  if (payload.overallStatus != null) {
    return {
      inputId: payload.inputId,
      overallStatus: payload.overallStatus,
      sourceStatus: payload.sourceStatus,
    };
  }

  const response = (await FuelToolsApiService.getInputStreamStatus(payload.inputId)) as Record<string, unknown>;
  return {
    inputId: payload.inputId,
    overallStatus: readOverallStatus(response),
    sourceStatus: readSourceStatus(response),
    liveProgramIds: response?.liveProgramIds as string[] | undefined,
    currentLiveProgramIds: response?.currentLiveProgramIds as string[] | undefined,
  };
}
